// Where the 3D page's artwork comes from: the gallery's own API.
//
// The art used to come from the bundle — `require.context` over Assets/Images/Categories/*,
// in AddImages.js — so the page could only ever show what was in its own folders at build
// time. It now comes from `api/images.php`, the same endpoint the gallery renders from, so
// one admin panel feeds both and art changes without a rebuild.
//
// Everything that has to move between the API's shape and the page's shape is here, and it is
// all pure: no three.js, no DOM, no globals. That is what makes it testable under jsdom,
// which cannot import the 3D chain (a sphere, a font and a WebGL scene are built on the way
// in). The fetch is injected for the same reason.

/** The gallery's categories, in the order its own nav uses. The API only stores the keys. */
export const CATEGORIES = [
    { key: 'caricatures', label: 'Caricatures' },
    { key: 'comics', label: 'Comics' },
    { key: 'logo-design', label: 'Logo Design' },
    { key: 'nature', label: 'Nature' },
    { key: 'portraits', label: 'Portraits' },
    { key: 'vr', label: 'VR' },
];

/**
 * Which category each planet carries.
 *
 * Keyed by the planet's name, not by the label it is drawn with. The ring used to take its
 * category from the label text — `child.name.split('_')[0]` — and Jupiter's label is
 * `Web3D_VR`, so it asked for `Web3D`, found nothing and showed no art at all. Planets that
 * are not a gallery category (Mars is About Me, and Uranus and Neptune are under
 * construction) are simply absent, which reads as "no ring". Saturn carries Logo Design,
 * which was the last gallery category without a planet.
 */
const PLANET_CATEGORY = {
    Sun: 'caricatures',
    Mercury: 'comics',
    Venus: 'nature',
    Earth: 'portraits',
    Jupiter: 'vr',
    Saturn: 'logo-design',
};

const CATEGORY_KEYS = CATEGORIES.map(c => c.key);

/** A URL that already knows where it lives: a scheme, a protocol-relative path, or rooted. */
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;

/**
 * The API answers with paths relative to the web root — `uploads/abc123.jpg`, built from
 * UPLOAD_URL in api/config.php — because the gallery is served from the root and can resolve
 * them by doing nothing. This page is served from a folder, so the same string would be
 * fetched as /web3DInteractive/uploads/abc123.jpg and 404. Everything that leaves here is
 * absolute.
 */
export function absoluteUrl(url, base = '/') {
    if (!url) return '';
    if (ABSOLUTE_URL.test(url) || url.startsWith('//')) return url;
    if (url.startsWith('/')) return url;

    return base.replace(/\/+$/, '') + '/' + url.replace(/^\.?\//, '');
}

/** The category a planet carries, or undefined if it is not one of the gallery's. */
export function categoryFor(planetName) {
    return PLANET_CATEGORY[planetName];
}

/**
 * Where the gallery is, seen from this page.
 *
 * The app is served from <site>/web3DInteractive/ and the gallery's API and uploads from
 * <site>/, so the gallery root is this page's parent folder. It is resolved against the
 * document's own URL rather than written down as '/', because <site> is the domain root in
 * production and a subfolder on a test site — pasamson.net/test — where a leading slash would
 * point the API and every upload above the gallery.
 */
export function galleryBase(documentBaseUri) {
    return new URL('../', documentBaseUri).href;
}

/**
 * The API's flat list, one array per category.
 *
 * The record is the only thing standing between the API and three consumers — the orbiting
 * ring picks a texture from `thumbUrl`, falling back to the full-size `url`, the carousel
 * paints from `url`, and the viewer needs `url`, `title` and `mediaType` when a thumbnail is
 * clicked — so it carries all of them, under the names the gallery itself uses for the same
 * fields.
 *
 * Categories come out in the gallery's order rather than the API's. The API sorts newest
 * first, so a freshly uploaded Comic would otherwise take the carousel's first page and push
 * everything along.
 */
export function groupByCategory(items, base = '/') {
    const byKey = {};

    (Array.isArray(items) ? items : []).forEach(item => {
        const key = item && item.category;
        if (CATEGORY_KEYS.indexOf(key) === -1) return;

        const url = absoluteUrl(item.url || item.dataUrl, base);
        if (!url) return;

        if (!byKey[key]) byKey[key] = [];
        byKey[key].push({
            id: item.id,
            name: item.filename || item.name || '',
            title: item.title || item.name || '',
            url,
            // The gallery's own downscaled copy of this work, when it offers one —
            // `api/thumb.php?f=…&w=640`. It rides beside `url` rather than replacing it because
            // the two are the same picture at two sizes: a card on the ring is drawn about 200 px
            // wide and the GPU copy is capped at 2048 only *after* the bytes arrive, so fetching
            // the original spends up to 17 MB and 42 MP on a thumbnail; the viewer, one click
            // later, wants exactly that original. Null when there is none to have — a clip, whose
            // card is the clip itself, or anything a host without GD could not build one for.
            thumbUrl: item.thumbUrl ? absoluteUrl(item.thumbUrl, base) : null,
            mediaType: item.mediaType === 'video' ? 'video' : 'image',
        });
    });

    const grouped = {};
    CATEGORY_KEYS.forEach(key => {
        if (byKey[key]) grouped[key] = byKey[key];
    });

    return grouped;
}

/**
 * One request, everything the page shows.
 *
 * A failure is thrown rather than swallowed: an empty result is indistinguishable from a
 * gallery with no art in it, and a page that quietly shows nothing is much harder to diagnose
 * than one that says what went wrong.
 */
export async function loadArt({ fetchImpl = fetch, base = '/' } = {}) {
    const response = await fetchImpl(absoluteUrl('api/images.php', base));

    if (!response.ok) {
        throw new Error(`artSource: api/images.php answered ${response.status}`);
    }

    const body = await response.json();
    if (!Array.isArray(body)) {
        throw new Error('artSource: api/images.php did not answer with a list');
    }

    return groupByCategory(body, base);
}
